import {memo, useImperativeHandle} from 'react'
import styles from './RailsPartial.module.css'
import {clsx} from 'clsx'
import type {QueryRoute} from '@github-ui/react-core/future/query-route'
import {useRouteQuery} from '@github-ui/react-core/future/use-route-query'
import type {SafeHtml} from '@github-ui/dotcom-schema/types/common/v1/safe_html'

export type SafeRailsHTML = string & {__safeRailsHTMLBrand: never}

export interface RailsPartialHandle {
  refetch: () => void
}

type BaseRailsPartialProps = Omit<
  React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement>,
  'ref'
> & {
  skeleton?: React.ReactNode
}

export type RailsPartialProps = BaseRailsPartialProps & {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  route: QueryRoute<string, string, string, Record<string, any>>
  partialName: string
  ref?: React.Ref<RailsPartialHandle>
}

type RailsPartialHTMLProps = BaseRailsPartialProps & {
  partialName: string
  // `SafeHtml` once the backend has rolled out proto-encoded partial payloads for
  // this route; a raw string when running against a backend that hasn't (master
  // as of writing). The SSR-embedded marker/payload is only decoded into a
  // `SafeHtml` message when the backend flags the route under
  // `meta.protoRoutes` (see `DECODE_EMBEDDED_PROTO` in `rails-partial-query.ts`)
  // — otherwise it passes through untouched as whatever the backend embedded.
  html: SafeHtml | SafeRailsHTML | undefined
  isLoading?: boolean
}

const RailsPartialUnmemoized = ({ref, route, partialName, className, skeleton, ...props}: RailsPartialProps) => {
  const fullPartialName = `${route.id}.${partialName}`
  const {data: html, refetch, isLoading} = useRouteQuery(route, fullPartialName)

  useImperativeHandle(ref, () => ({
    refetch,
  }))

  return (
    <RailsPartialHTML
      partialName={fullPartialName}
      html={html}
      className={className}
      skeleton={skeleton}
      isLoading={isLoading}
      {...props}
    />
  )
}
RailsPartialUnmemoized.displayName = 'RailsPartial'

// eslint-disable-next-line @typescript-eslint/no-shadow -- named function expression intentionally shares the outer name
export const RailsPartialHTML = memo(function RailsPartialHTML({
  partialName,
  html,
  className,
  skeleton,
  isLoading = false,
  ...props
}: RailsPartialHTMLProps) {
  const htmlString = typeof html === 'string' ? html : html?.value

  if (!htmlString) return isLoading ? skeleton : null

  return (
    <rails-partial
      data-partial-name={partialName}
      class={clsx(styles['d-contents'], className)}
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{__html: htmlString}}
      suppressHydrationWarning
      {...props}
    />
  )
})

const RailsPartial = memo(RailsPartialUnmemoized)

export {RailsPartial}
